/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 5 hook(s) need dependency review; fix each and remove this directive */
import React, { forwardRef, useId, useState, useEffect, useRef, useCallback } from "react";
import "./index.css";
import {
  SlickgridReact,
  SlickRowDetailView,
  ExtensionName,
  type SlickgridReactInstance,
  type GridOption,
  type Column,
  type OnScrollEventArgs,
  type OnClickEventArgs,
  type SlickDataView,
  type SlickGrid as SlickGridType,
} from "slickgrid-react";
import "@slickgrid-universal/common/dist/styles/css/slickgrid-theme-material.css";
import PagerComponent from "./Pagination";
import CircularProgress from "@mui/material/CircularProgress";
import Shimmer from "../../../components/Queries/Shimmer";
import { ExcelExportService } from "@slickgrid-universal/excel-export";
import ToolBar, { ToolbarItem } from "./ToolBar";
import type { ColumnDefinition as ToolBarColumn } from "./ToolBar";
import FilterComponent from "./FilterComponent";
import type { ColumnDefinition as FilterColumn, Props as FilterComponentProps } from "./FilterComponent";
import axiosWrapper from "../../../services/axiosWrapper";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import Text from "../Text/Text";
import { setSelectedRowsSafe } from "../gridSelection";
import get from "lodash/get";
import debounce from "lodash/debounce";
import dayjs, { Dayjs } from "dayjs";
import { Box } from "@mui/material";
import { useTranslation, untranslated } from "@i18n";
import DOMPurify from "dompurify";
import type { Formatter } from "@slickgrid-universal/common";
import { cloneDeep } from "lodash";

/** Internal types for SlickGrid metadata and row spans */
interface RowMetadata {
  columns?: Record<number, { rowspan: number }>;
  cssClasses?: string;
}

interface RowSpanResult<T extends GridRowBase> {
  newDataset: T[];
  metadata: Record<number, RowMetadata>;
}

/** Filter condition stored for each column */
interface FilterCondition {
  id: string;
  type: FilterType;
  option: QueryPredicate["operator"];
  text: unknown;
}

/** Internal type for row move event data */
interface RowMoveData {
  insertBefore: number;
  rows: number[];
}

/** Internal type for column reorder event args */
interface ColumnsReorderedArgs {
  impactedColumns: Column[];
}

type SlickEventArgs = Record<string, unknown>;
type FilterType = "Text" | "Date" | "Number" | "MultiSelect";
/** Row ID type - supports string, number, or ObjectId-like objects */
type RowId = string | number | { toString(): string };
type ParentId = string | number | null;

export type GridRowBase = {
  id?: RowId;
  isRowDisabled?: boolean;
  __isDummy?: boolean;
  __parentId?: string;
  disableBorder?: boolean;
  __hasChildren?: boolean;
  __collapsed?: boolean;
  parentId?: RowId | null;
  indentLevel?: number;
  treeLevel?: number;
  hasChild?: boolean;
  isExpanded?: boolean;
  _id?: RowId;
};

export interface ColumnHeaderButton {
  cssClass?: string;
  command?: string;
  tooltip?: string;
}

export interface ColumnHeader {
  buttons?: ColumnHeaderButton[];
}

/**
 * Column definition interface for SlickGrid columns.
 * The `field` property must be a valid key of your row type.
 * The `formatter` value parameter will be typed based on the field.
 */
export interface ColumnDefinition<T extends GridRowBase, K extends keyof T = keyof T> {
  id: string | number;
  /** Field name - must be a key of the row type T */
  field: K;
  name?: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  sortable?: boolean;
  /** Alias for sortable */
  allowSorting?: boolean;
  filterable?: boolean;
  /** Allow filtering on this column */
  allowFiltering?: boolean;
  groupable?: boolean;
  type?: string;
  header?: ColumnHeader;
  wrapText?: boolean;
  filterType?: FilterType;
  excelExportOptions?: unknown;
  headerTemplate?: (args: { column: ColumnDefinition<T, K>; onClick: (e: unknown) => void }) => React.ReactNode;
  /** Formatter function - value type is flexible to support various column types */

  // Method shorthand, not an arrow property: under strictFunctionTypes an arrow-typed callback
  // checks its parameters contravariantly, which rejects every call site that annotates
  // `value` with the concrete cell type its own `field` implies. Method shorthand makes the
  // parameters bivariant, so those narrower formatters are accepted again.
  formatter?(row: number, cell: number, value: unknown, columnDef: unknown, dataContext: T, grid?: unknown): unknown;
  editorTemplate?: (args: { value: T[K]; updatedColumn: ColumnDefinition<T, K>; args: T }) => React.ReactNode;
  /** Custom value getter - returns computed value for sorting/filtering/display */
  _valueGetter?: (row: T) => unknown;
  hidden?: boolean;
  /** Whether the column is visible */
  visible?: boolean;
  /** Text alignment for the column */
  textAlign?: "Left" | "Right" | "Center" | string;
  /** Mark column as primary key for row identification */
  isPrimaryKey?: boolean;
}

/**
 * Brand symbol for validated column definitions.
 * This creates a "nominal" type that plain objects cannot satisfy.
 */
declare const _ValidatedColumnBrand: unique symbol;
type ValidatedColumnBrandType = typeof _ValidatedColumnBrand;

/**
 * Validated column definition type - can only be created via `typedColumns()`.
 * Plain object literals will NOT be assignable to this type.
 */
export type ValidatedColumnDefinition<T extends GridRowBase> = ColumnDefinition<T, keyof T> & {
  /** @internal Brand to ensure columns are created with typedColumns() */
  readonly __brand: ValidatedColumnBrandType;
};

/**
 * Creates validated, type-safe column definitions for SlickGrid.
 *
 * REQUIRED: All SlickGrid columns must be created with this function
 * to ensure type safety between field names and formatter value types.
 *
 * @example
 * interface ResourceRow extends GridRowBase {
 *   counter: number;
 *   name: string;
 *   selectionSetIds?: string[];
 * }
 *
 * const columns = typedColumns<ResourceRow>([
 *   {
 *     id: "name",
 *     field: "name",  // ✅ TypeScript checks this is keyof ResourceRow
 *     formatter: (row, cell, value, col, data) => {
 *       // value is typed as `string`
 *       return value.toUpperCase();
 *     }
 *   },
 *   {
 *     id: "selectionSetIds",
 *     field: "selectionSetIds",
 *     formatter: (row, cell, value, col, data) => {
 *       // value is typed as `string[] | undefined`
 *       // TypeScript REQUIRES you to check for undefined!
 *       if (!Array.isArray(value)) return "";
 *       return value.join(", ");
 *     }
 *   }
 * ]);
 */
export function typedColumns<TRow extends GridRowBase>(
  columns: Array<
    {
      [K in keyof TRow]: {
        id: string | number;
        field: K;
        name?: string;
        width?: number;
        minWidth?: number;
        maxWidth?: number;
        sortable?: boolean;
        allowSorting?: boolean;
        filterable?: boolean;
        allowFiltering?: boolean;
        groupable?: boolean;
        type?: string;
        header?: ColumnHeader;
        wrapText?: boolean;
        filterType?: FilterType;
        excelExportOptions?: unknown;
        headerTemplate?: (args: {
          column: ColumnDefinition<TRow, K>;
          onClick: (e: unknown) => void;
        }) => React.ReactNode;

        formatter?(
          row: number,
          cell: number,
          value: unknown,
          columnDef: unknown,
          dataContext: TRow,
          grid?: unknown,
        ): unknown;
        editorTemplate?: (args: {
          value: TRow[K];
          updatedColumn: ColumnDefinition<TRow, K>;
          args: TRow;
        }) => React.ReactNode;
        _valueGetter?: (row: TRow) => unknown;
        _excelValueGetter?: (row: TRow) => unknown;
        excludeFromExport?: boolean;
        hidden?: boolean;
        visible?: boolean;
        textAlign?: "Left" | "Right" | "Center" | string;
        isPrimaryKey?: boolean;
      };
    }[keyof TRow]
  >,
): ValidatedColumnDefinition<TRow>[] {
  return columns as ValidatedColumnDefinition<TRow>[];
}

export type QueryOperator =
  | "equal"
  | "greaterthan"
  | "greaterthanorequal"
  | "lessthan"
  | "lessthanorequal"
  | "notequal"
  | "null"
  | "notnull"
  | "startswith"
  | "endswith"
  | "contains"
  | "doesnotstartwith"
  | "doesnotendwith"
  | "doesnotcontain"
  | "like"
  | "empty"
  | "notempty"
  | "between";

export interface QueryPredicate {
  field: string;
  operator: QueryOperator;
  value: unknown;
  isComplex?: boolean;
  ignoreCase?: boolean;
  ignoreAccent?: boolean;
  /** Additional custom predicate properties */
  condition?: string;
  predicates?: QueryPredicate[];
}

export interface QuerySortInfo {
  name: string;
  direction: "ascending" | "descending";
}

export interface QueryShape {
  where?: Array<QueryPredicate | Record<string, unknown>>;
  skip?: number;
  take?: number;
  requiresCounts?: boolean;
  sorted?: QuerySortInfo[];
  nextPageToken?: string | null;
  /** Additional query parameters for backend APIs */
  select?: string[];
  expand?: string[];
  search?: string;
  /** Custom parameters passed to backend */
  params?: Record<string, unknown>;
  /** Allow additional arbitrary fields for flexibility */
  [key: string]: unknown;
}

export interface ExcelExportConfig {
  filename?: string;
  sheetName?: string;
  exportWithFormatter?: boolean;
}

export interface SlickGridProps<T extends GridRowBase> extends Required<Pick<SlickGridProps<T>, "columnDefinitions">> {
  id?: string;
  showPageCount?: boolean;
  infiniteScroll?: boolean;
  showLoader?: boolean;
  disableHeader?: boolean;
  disableMarginForNoData?: boolean;
  enableWrapText?: boolean;
  enableTreeEllipsis?: boolean;
  disableRepaintOnDataChange?: boolean;
  dataset?: T[];
  datasetHierarchical?: T[];
  surpassedDataset?: T[];
  /**
   * Column definitions - MUST be created with `typedColumns<T>()` for type safety.
   *
   * @example
   * import { typedColumns } from "@CubeComponents/html/SlickGrid";
   *
   * const columns = typedColumns<MyRowType>([
   *   { id: "name", field: "name", name: "Name" },
   *   { id: "count", field: "count", name: "Count" }
   * ]);
   *
   * <SlickGrid dataset={data} columnDefinitions={columns} />
   */
  columnDefinitions: ValidatedColumnDefinition<T>[];
  query?: QueryShape;
  beforeDataBound?: (resp: unknown) => T[];
  remoteDataSource?: string;
  allowPagination?: boolean;
  allowGrouping?: boolean;
  gridOptions?: Partial<GridOption>;
  enableFiltering?: boolean;
  disableCustomSorting?: boolean;
  disableSorting?: boolean;
  enableColumnChooser?: boolean;
  enableMultiSelect?: boolean;
  onSelectedRowIdsChanged?: (args: SlickEventArgs) => void;
  disabledIds?: Array<RowId>;
  selectCheckBoxOnRowClick?: boolean;
  onGroupChanged?: (args: unknown) => void;
  onExtensionRegistered?: (ext: unknown) => void;
  setGridReady?: (ready: boolean) => void;
  onReactGridCreated?: (instance: SlickgridReactInstance) => void;
  toolbar?: ToolbarItem[];
  toolbarClickHandler?: (cmd: unknown) => void;
  showToolbarBorder?: boolean;
  callbackForVisibleColumnsUpdate?: (columns: ValidatedColumnDefinition<T>[]) => void;
  toolbarHeight?: number | string;
  propertiesPanel?: boolean;
  excelExport?: boolean;
  isEditMode?: boolean;
  handleUpdateCurrentView?: (payload: { columns: Array<string | number> }) => void;
  onClick?: (e: SlickEventArgs, args: SlickEventArgs) => void;
  onDblClick?: (e: { detail: { args: { row: number; cell: number } } }) => void;
  onFilterChange?: (activeKeys: string[], conditions: Record<string, FilterCondition>) => void;
  treeGrid?: boolean;
  hasChild?: boolean;
  disableRowBorder?: boolean;
  hasChildMapping?: string;
  idMapping?: string;
  parentIdMapping?: string;
  titleFormatter?: Formatter;
  initialSortColumnId?: string | number;
  dropdownColumn?: number;
  disableChildDataFetch?: boolean;
  childGridComponent?: React.ComponentType<{ row: T }> | React.FC<{ row: T }> | object;
  dragAndDrop?: boolean;
  disableTreeCollapseOnDataChange?: boolean;
  rowDrop?: (payload: { parentId: ParentId; movedRowData: T }) => Promise<boolean>;
  initialSortDirection?: "ASC" | "DESC";
  enableSelectAllCheckbox?: boolean;
  enablePointerForRows?: boolean;
  preAppliedFilters?: Record<string, FilterCondition>;
  enableGridMenu?: boolean;
  enableColumnPicker?: boolean;
  disableFullHeight?: boolean;
  onColumnsReordered?: (e: Event, args: ColumnsReorderedArgs) => void;
  renderOption?: (option: unknown) => React.ReactNode;
  /** Disable virtualisation for smaller datasets */
  disableVirtualisation?: boolean;
}

type SlickgridExtras<T extends GridRowBase> = {
  addRecord?: (rowToAdd: T, position?: number) => void;
  removeRecord?: (position?: number) => void;
  refresh?: () => void;
  resetPagination?: (skipApiCall?: boolean) => void;
  appliedFilters?: string[];
  deleteRow?: boolean;
  actual?: unknown;
};
/** Exported because TreeView.tsx types its grid handle with it (it mounts this grid). */
export type SlickgridReactInstanceX<T extends GridRowBase> = SlickgridReactInstance & SlickgridExtras<T>;

const NO_OF_FETCHED_ROWS = 1000000;
const FETCH_SIZE = 30;
const INITIAL_WRAP_RUN_DELAY_MS = 2000;
const DRAG_TOLERANCE_PX = 10;

const DEFAULT_FILTER_OPTIONS: Record<
  Exclude<FilterType, "MultiSelect">,
  { option: QueryPredicate["operator"]; text: unknown }
> = {
  Text: { option: "startswith", text: "" },
  Date: { option: "equal", text: { date: dayjs() } },
  Number: { option: "equal", text: 0 },
};

function isNil(v: unknown): v is null | undefined {
  return v === null || v === undefined;
}

function toLowerString(v: unknown): string {
  if (v == null) return "";

  switch (typeof v) {
    case "string":
    case "number":
    case "boolean":
      return String(v).toLowerCase();

    case "object":
      try {
        return JSON.stringify(v).toLowerCase();
      } catch {
        return "";
      }

    default:
      return "";
  }
}

type DebouncedFunction<TArgs extends unknown[]> = ((...args: TArgs) => void) & { cancel: () => void };

function makeDebounce<TArgs extends unknown[]>(fn: (...args: TArgs) => void, delay: number): DebouncedFunction<TArgs> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const debounced = (...args: TArgs) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
  debounced.cancel = () => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = null;
  };
  return debounced;
}

function getRowElementByIndex(index: number): Element | null {
  const odd = document.querySelectorAll(".slick-row.odd");
  const even = document.querySelectorAll(".slick-row.even");
  return index % 2 === 0 ? (even[index / 2] ?? null) : (odd[Math.floor(index / 2)] ?? null);
}

function isMidpointHit(dragTop: number, rowEl: Element | null): boolean {
  if (!rowEl) return false;
  const rect = (rowEl as HTMLElement).getBoundingClientRect();
  const midY = rect.top + rect.height / 2;
  return dragTop >= midY - DRAG_TOLERANCE_PX && dragTop <= midY + DRAG_TOLERANCE_PX;
}

function getItemsSourceSafe<T extends GridRowBase>(dv: SlickDataView<T> | null | undefined): T[] {
  if (!dv) return [];
  if (typeof dv.getFilteredItems === "function") return dv.getFilteredItems();
  if (typeof dv.getItems === "function") return dv.getItems();
  return [];
}

function getDroppedRowIndex(insertBefore: number): number | null {
  const idx = insertBefore - 1;
  return idx >= 0 ? idx : null;
}

function getRowElementsSafe(movedIdx: number, droppedIdx: number) {
  const movedEl = getRowElementByIndex(movedIdx);
  const droppedEl = getRowElementByIndex(droppedIdx);
  if (!movedEl || !droppedEl) return null;
  return { movedEl, droppedEl };
}

interface HideGuideOptions<T extends GridRowBase> {
  movedElTop: number;
  rowRectTop: number;
  droppedRowData: T | null;
  movedRowData: T;
  currentTop: number;
  droppedEl: Element;
  insertBefore: number | null;
}

function shouldHideGuide<T extends GridRowBase>(opts: HideGuideOptions<T>): boolean {
  const { movedElTop, rowRectTop, droppedRowData, movedRowData, currentTop, droppedEl, insertBefore } = opts;
  if (movedElTop < rowRectTop) return true;
  if (!droppedRowData) return true;
  const differentParent = droppedRowData.parentId !== movedRowData.parentId;
  const midpointHit = isMidpointHit(currentTop, droppedEl);
  if (differentParent && !midpointHit && insertBefore !== 0) return true;
  return false;
}

function setGuide(reorderGuide: HTMLElement, show: boolean, highlight: boolean) {
  reorderGuide.style.display = show ? "block" : "none";
  if (!show) {
    reorderGuide.classList.remove("parent-child-move");
    return;
  }
  if (highlight) reorderGuide.classList.add("parent-child-move");
  else reorderGuide.classList.remove("parent-child-move");
}

const mapSpecialColumn = <T extends GridRowBase>(c: Column<T>): ColumnDefinition<T> => {
  return {
    ...c,
    id: c.id as string | number,
    field: c.field as keyof T,
    sortable: false,
    filterable: false,
    groupable: false,
    reorderable: false,
    selectable: false,
  } as ColumnDefinition<T>;
};

function createMainRow<T extends GridRowBase>(item: T, idxCompressed: number, requiredRows: number): T {
  const rowId = item.id ?? `row_${idxCompressed}`;
  return {
    ...item,
    id: rowId,
    disableBorder: requiredRows > 1,
  };
}

function createDummyRows<T extends GridRowBase>(item: T, mainRowId: RowId, requiredRows: number): T[] {
  const dummies: T[] = [];
  for (let iIdx = 1; iIdx < requiredRows; iIdx++) {
    dummies.push({
      ...item,
      __isDummy: true,
      __parentId: String(mainRowId),
      id: `${mainRowId}_dummy_${iIdx}_${crypto.randomUUID()}`,
    } as T);
  }
  return dummies;
}

function buildMetadataForRow<T extends GridRowBase>(
  columnDefs: ColumnDefinition<T>[],
  requiredRows: number,
): RowMetadata {
  const columnsMeta: Record<number, { rowspan: number }> = {};
  for (const [colIdx] of columnDefs.entries()) {
    columnsMeta[colIdx] = { rowspan: requiredRows };
  }
  return { columns: columnsMeta };
}

const SlickGridInner = forwardRef(function SlickGridInner<T extends GridRowBase = GridRowBase>(
  props: SlickGridProps<T>,
  ref: React.ForwardedRef<SlickgridReactInstanceX<T>>,
) {
  const { t } = useTranslation();
  const { showPageCount = true } = props;

  const generatedId = useId();
  const id = props.id || generatedId;
  const standByRef = useRef<SlickgridReactInstanceX<T> | null>(null);

  const gridRef = (ref as React.MutableRefObject<SlickgridReactInstanceX<T> | null>) || standByRef;

  const excelExportService = React.useMemo(() => new ExcelExportService(), []);
  useEffect(() => {
    (globalThis as typeof globalThis & { excelExportService?: ExcelExportService }).excelExportService =
      excelExportService;
  }, [excelExportService]);

  const [appliedFilters, setAppliedFilters] = useState<string[]>([]);
  const [filterConditions, setFilterConditions] = useState<Record<string, FilterCondition>>({});
  const [loaderOnPageChange, setLoaderOnPageChange] = useState<boolean>(!!props.showLoader);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItemCount, setTotalItemCount] = useState<number>(1);
  const [allPrerequisiteLoaded, setAllPrerequisiteLoaded] = useState<boolean>(false);
  const [cssClasses, setCssClasses] = useState<string>("");

  const isEditModeRef = useRef<boolean>(false);
  const nextPageTokenRef = useRef<string | null>(null);
  const isLastRef = useRef<boolean>(false);

  const [loaderForInfiniteScroll, setLoaderForInfiniteScroll] = useState<boolean>(false);
  const [currentLoadedCount, setCurrentLoadedCount] = useState<number>(0);

  useEffect(() => {
    isEditModeRef.current = !!props.isEditMode;
  }, [props.isEditMode]);

  const [totalPages, setTotalPages] = useState<number>(
    Math.ceil(totalItemCount / pageSize) === 0 ? 1 : Math.ceil(totalItemCount / pageSize),
  );
  useEffect(() => {
    setTotalPages(Math.max(1, Math.ceil(totalItemCount / pageSize)));
  }, [pageSize, totalItemCount]);
  useEffect(() => {
    setCurrentPage(1);
  }, [totalPages]);

  const [dataset, setDataset] = useState<T[]>([]);
  const [updatedColumnDefinitions, setUpdatedColumnDefinitions] = useState<ColumnDefinition<T>[]>([]);
  const [sortingDetails, setSortingDetails] = useState<false | { columnId: string; sortAsc: boolean }>(false);

  const [currentColumnId, setCurrentColumnId] = useState<string>("");
  const [filterPredicates, setFilterPredicates] = useState<Record<string, unknown> | string>("");
  const [anchorEl, setAnchorEl] = useState<{ top: number; left: number } | null>(null);
  const [filterType, setFilterType] = useState<FilterType | "">("");
  const [filterOpened, setFilterOpened] = useState<boolean>(false);
  const [excelExportExtension, setExcelExportExtension] = useState<ExcelExportService | null>(null);

  const topOfDragRowRef = useRef<number | undefined>(undefined);
  const [insertBefore, setInsertBefore] = useState<number | null>(null);
  const [dataRow, setDataRow] = useState<number | null>(null);
  const [top, setTop] = useState<number | null>(null);

  interface GridStateSnapshot<TRow extends GridRowBase> {
    pageSize: number;
    currentPage: number;
    query: QueryShape | undefined;
    originalDataset: TRow[] | undefined;
    sortingDetails: false | { columnId: string; sortAsc: boolean };
    appliedFilters: string[];
    dataset: TRow[];
    filterConditions: Record<string, FilterCondition>;
    filterPredicates: Record<string, unknown> | string;
    currentColumnId: string;
    totalItemCount: number;
  }
  const stateRef = useRef<GridStateSnapshot<T> | undefined>(undefined);
  const [gridMounted, setGridMounted] = useState<boolean>(false);

  const specialColumnRef = useRef<ColumnDefinition<T>[]>([]);
  const [selectedItems, setSelectedItems] = useState<number>(0);

  useEffect(() => {
    const cls: string[] = [];
    if (props.disableHeader) cls.push("disable-header");
    if (props.disableMarginForNoData) cls.push("disable-margin-no-data");
    if (props.enableSelectAllCheckbox && dataset.length > 0) cls.push("enable-selectall-checkbox");
    if (props.enableWrapText) cls.push("enable-wrap-text");
    if (props.enableTreeEllipsis) cls.push("tree-row-ellipsis");
    if (props.disableRowBorder) cls.push("disable-row-border");
    if (props.enablePointerForRows) cls.push("cursor-pointer-row");
    if (props.disableFullHeight) cls.push("disable-full-height");
    setCssClasses(cls.join(" "));
  }, [
    props.disableHeader,
    props.disableMarginForNoData,
    props.enableSelectAllCheckbox,
    props.enableWrapText,
    props.enableTreeEllipsis,
    props.disableRowBorder,
    props.enablePointerForRows,
    dataset.length,
    props.disableFullHeight,
  ]);

  useEffect(() => {
    if (dataset && props.columnDefinitions.length) {
      setAllPrerequisiteLoaded(true);
    }
  }, [dataset, props.columnDefinitions]);

  useEffect(() => {
    if (props.infiniteScroll && dataset) {
      setCurrentLoadedCount(dataset?.length);
    }
  }, [dataset, props.infiniteScroll]);

  useEffect(() => {
    stateRef.current = {
      pageSize,
      currentPage,
      query: props.query,
      originalDataset: props.dataset,
      sortingDetails,
      appliedFilters,
      dataset,
      filterConditions,
      filterPredicates,
      currentColumnId,
      totalItemCount,
    };
  });

  useEffect(() => {
    return () => {
      gridRef.current = null;
    };
  }, [gridRef]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      (globalThis as typeof globalThis & { gridRef1?: unknown }).gridRef1 = gridRef;
      return () => {
        (globalThis as typeof globalThis & { gridRef1?: unknown }).gridRef1 = undefined;
      };
    }
    return () => undefined;
  }, [gridRef]);

  useEffect(() => {
    const current = gridRef.current;
    if (current) {
      current.addRecord = (rowToAdd: T, position?: number) => {
        const currentItems = [...(current.dataView.getItems() as T[])];
        const finalPosition =
          position === undefined || position < 0 || position > currentItems.length ? currentItems.length : position;

        const updatedItems = [...currentItems.slice(0, finalPosition), rowToAdd, ...currentItems.slice(finalPosition)];
        setDataset(updatedItems);

        setTimeout(() => {
          current.slickGrid?.setSelectedRows([finalPosition]);
        }, 0);
      };

      current.removeRecord = (position?: number) => {
        if (position === undefined) return;
        const updatedItems = cloneDeep(current.dataView.getItems() as T[]);
        if (position >= 0 && position < updatedItems.length) {
          updatedItems.splice(position, 1);
          setDataset(updatedItems);
        }
      };

      current.refresh = () => {
        current.slickGrid?.setSelectedRows([]);
        handleDataLoad();
      };

      current.resetPagination = (skipApiCall = false) => {
        nextPageTokenRef.current = null;
        isLastRef.current = false;
        setCurrentPage(1);
        if (!skipApiCall) {
          handleDataLoad();
        }
      };
    }
  });

  const ellipseFormatterWithTooltip = (args: { value: unknown; column: ColumnDefinition<T> }) => {
    const cellValue = (args.value ?? "") as string | number;
    const container = document.createElement("div");
    container.style.width = "100%";
    container.style.overflow = "hidden";
    const root = createRoot(container);
    root.render(<Text tooltipContent={untranslated(String(cellValue))}>{String(cellValue)}</Text>);
    return container;
  };

  function buildBaseFormatter(column: ColumnDefinition<T>, isEditMode: boolean) {
    if (!column.formatter && !(isEditMode && column.editorTemplate)) {
      return (_row: number, _cell: number, _value: unknown, _columnDef: unknown, dataContext: T) => {
        if (dataContext.__isDummy) return "";
        const rawValue = column._valueGetter
          ? column._valueGetter(dataContext)
          : get(dataContext, column.field as string, "");
        const cls = (column as ColumnDefinition<T> & { spanClass?: string }).spanClass || "sg-cell";
        if (props.enableWrapText) {
          return `<span class="${cls}" title="${rawValue}">${rawValue}</span>`;
        } else {
          return ellipseFormatterWithTooltip({
            value: rawValue,
            column,
          });
        }
      };
    }

    if (column.formatter && !(isEditMode && column.editorTemplate)) {
      const self = column.formatter;
      return (row: number, cell: number, value: unknown, columnDef: unknown, dataContext: T, grid?: unknown) => {
        if ((dataContext as GridRowBase)?.__isDummy) return "";
        if (typeof self === "function") {
          // Cast value to the expected type - slickgrid-react provides the correct runtime value
          return self(row, cell, value as T[keyof T], columnDef, dataContext, grid);
        } else {
          console.log("Formatter is not a function");
          return "";
        }
      };
    }

    return undefined;
  }
  const metadataRef = useRef<Record<number, RowMetadata>>({});

  function getRenderedLines(
    slickGrid: SlickGridType,
    dataView: SlickDataView<T>,
    itemId: RowId | undefined,
    colIndex: number,
    rowHeight: number,
  ): number {
    if (itemId == null) return 1;
    const renderedRowIndex = dataView.getRowById(String(itemId));
    if (renderedRowIndex == null || renderedRowIndex < 0) return 1;

    const cell = slickGrid.getCellNode(renderedRowIndex, colIndex);
    const requiredHeight = cell?.firstChild ? (cell.firstChild as HTMLElement).clientHeight : 0;
    return requiredHeight ? Math.ceil(requiredHeight / rowHeight) : 1;
  }

  function calculateMaxLinesForItem(
    item: T,
    columnDefs: Column<T>[],
    slickGrid: SlickGridType,
    dataView: SlickDataView<T>,
    rowHeight: number,
  ): number {
    let maxLines = 1;
    for (const [colIndex] of columnDefs.entries()) {
      const totalLines = getRenderedLines(slickGrid, dataView, item.id, colIndex, rowHeight);
      maxLines = Math.max(maxLines, totalLines);
    }
    return maxLines;
  }

  function calculateRowSpans(
    dataArr: T[],
    columnDefs: Column<T>[],
    slickGrid: SlickGridType,
    dataView: SlickDataView<T>,
    rowHeight: number,
  ): RowSpanResult<T> {
    const metadata: Record<number, RowMetadata> = {};
    const newDataset: T[] = [];

    for (const [idxCompressed, item] of dataArr.entries()) {
      if (item.__isDummy) continue;

      const maxRows = calculateMaxLinesForItem(item, columnDefs, slickGrid, dataView, rowHeight);
      const mainRow = createMainRow(item, idxCompressed, maxRows);
      const mainRowIndex = newDataset.length;

      newDataset.push(mainRow);

      if (maxRows > 1) {
        metadata[mainRowIndex] = buildMetadataForRow(columnDefs as unknown as ColumnDefinition<T>[], maxRows);
        const dummyRows = createDummyRows(item, mainRow.id!, maxRows);
        newDataset.push(...dummyRows);
      }
    }

    return { newDataset, metadata };
  }

  const onScroll = debounce(() => {
    const current = gridRef.current;
    if (!current) return;

    const slickGrid = current.slickGrid;
    const dataView = current.dataView;
    const allItems = dataView.getItems();

    const visibleRows = slickGrid.getRenderedRange();
    const endRow = Math.min(visibleRows.bottom + 1, allItems.length);
    const visibleData = allItems.slice(visibleRows.top, endRow);

    const rowHeight = slickGrid.getOptions().rowHeight || 48;
    const columns = slickGrid.getColumns();

    const { newDataset: processedVisibleItems, metadata: processedLocalMetadata } = calculateRowSpans(
      visibleData,
      columns,
      slickGrid,
      dataView,
      rowHeight,
    );

    const newFullDataset = [...allItems.slice(0, visibleRows.top), ...processedVisibleItems, ...allItems.slice(endRow)];

    const newFullMetadata = { ...metadataRef.current };

    for (let i = visibleRows.top; i < endRow; i++) {
      delete newFullMetadata[i];
    }

    for (const localIndex in processedLocalMetadata) {
      const globalIndex = Number(localIndex) + visibleRows.top;
      const meta = processedLocalMetadata[localIndex];
      if (meta) {
        newFullMetadata[globalIndex] = meta;
      }
    }

    metadataRef.current = newFullMetadata;

    requestAnimationFrame(() => {
      dataView.beginUpdate();
      dataView.setItems(newFullDataset);
      dataView.endUpdate();

      dataView.getItemMetadata = (rowIndex: number) => {
        const item = dataView.getItem(rowIndex);
        if (!item) return null;

        const cssClasses: string[] = [];
        if (item.__isDummy) cssClasses.push("dummy-row");
        if (item.disableBorder) cssClasses.push("disable-border");

        return {
          ...metadataRef.current[rowIndex],
          cssClasses: cssClasses.join(" "),
        };
      };

      slickGrid.invalidate();
      slickGrid.render();
    });

    setTimeout(() => {
      if (props.treeGrid) {
        gridRef.current?.treeDataService.toggleTreeDataCollapse(false);
      }
    }, 500);
  }, 200);

  const handleWrapUpdate = () => {
    const current = gridRef.current;
    if (!current) return;

    const slickGrid = current.slickGrid;
    const dataView = current.dataView;
    const columns = slickGrid.getColumns();
    const rawItems = dataView.getItems().filter((it) => !it.__isDummy);

    const rowHeight = slickGrid.getOptions().rowHeight || 48;
    const { newDataset, metadata } = calculateRowSpans(rawItems, columns, slickGrid, dataView, rowHeight);

    metadataRef.current = metadata;

    gridRef.current?.slickGrid?.remapAllColumnsRowSpan?.();

    requestAnimationFrame(() => {
      dataView.beginUpdate();
      dataView.setItems(newDataset);
      dataView.endUpdate();

      dataView.getItemMetadata = (rowIndex: number) => {
        const item = dataView.getItem(rowIndex);
        if (!item) return null;

        const cssClasses: string[] = [];
        if (item.__isDummy) cssClasses.push("dummy-row");
        if (item.disableBorder) cssClasses.push("disable-border");

        return {
          ...metadataRef.current[rowIndex],
          cssClasses: cssClasses.join(" "),
        };
      };

      slickGrid.invalidate();
      slickGrid.render();
    });

    setTimeout(() => {
      if (props.treeGrid) {
        gridRef.current?.treeDataService.toggleTreeDataCollapse(false);
      }
    }, 500);
  };

  useEffect(() => {
    if (!props.enableWrapText) return;
    const sg = gridRef?.current?.slickGrid;
    const dataView = gridRef?.current?.dataView;
    if (!sg || !dataView) return;
    const originalFilter = dataView.getFilter();
    dataView.setFilter((item: T, args?: unknown) => {
      if (item.__isDummy) return true;
      return originalFilter ? originalFilter(item, args) : true;
    });

    handleWrapUpdate();
    const onResize = makeDebounce(handleWrapUpdate, 150);
    const timer = setTimeout(() => handleWrapUpdate(), 2000);

    sg.onColumnsResized.subscribe(onResize);
    sg.onViewportChanged.subscribe(onScroll);
    return () => {
      clearTimeout(timer);
      sg.onColumnsResized.unsubscribe(onResize);
      sg.onViewportChanged.unsubscribe(onScroll);
    };
  }, [props.enableWrapText, dataset]);

  useEffect(() => {
    if (!props.disableRepaintOnDataChange && gridRef.current) {
      gridRef.current?.slickGrid?.invalidate();
    }
  }, [dataset, gridRef, props.disableRepaintOnDataChange]);

  /** Date filter value can be a Dayjs object or object with date property */
  type DateFilterValue = Dayjs | { date: Dayjs } | string | number;

  const matchDateFilter = (fieldValue: unknown, filterOption: QueryOperator, filterValue: DateFilterValue): boolean => {
    const format = "MMM D,YYYY [at] hh:mm:ss A";
    const itemDate = Number.isNaN(Number(fieldValue)) ? dayjs(String(fieldValue), format) : dayjs(Number(fieldValue));
    // Extract the date value, handling both Dayjs objects and objects with date property
    const dateValue =
      typeof filterValue === "object" && filterValue !== null && "date" in filterValue ? filterValue.date : filterValue;
    // Convert to dayjs - if it's already a Dayjs, dayjs() will return it
    const filterDate = dayjs.isDayjs(dateValue) ? dateValue : dayjs(dateValue as string | number);

    switch (filterOption) {
      case "equal":
        return itemDate.isSame(filterDate, "day");
      case "greaterthan":
        return itemDate.isAfter(filterDate, "day");
      case "greaterthanorequal":
        return itemDate.isSame(filterDate, "day") || itemDate.isAfter(filterDate, "day");
      case "lessthan":
        return itemDate.isBefore(filterDate, "day");
      case "lessthanorequal":
        return itemDate.isSame(filterDate, "day") || itemDate.isBefore(filterDate, "day");
      case "notequal":
        return !itemDate.isSame(filterDate, "day");
      case "null":
        return !fieldValue || String(fieldValue).trim() === "";
      case "notnull":
        return !!fieldValue && String(fieldValue).trim() !== "";
      default:
        return false;
    }
  };

  const matchTextFilter = (fieldValue: unknown, filterOption: QueryOperator, filterValue: unknown): boolean => {
    const fieldValueStr = toLowerString(fieldValue);
    const filterValueLower = toLowerString(filterValue);

    switch (filterOption) {
      case "startswith":
        return fieldValueStr.startsWith(filterValueLower);
      case "endswith":
        return fieldValueStr.endsWith(filterValueLower);
      case "empty":
        return fieldValueStr === "";
      case "equal":
        return fieldValueStr === filterValueLower;
      case "contains":
      case "like":
        return fieldValueStr.includes(filterValueLower);
      case "doesnotstartwith":
        return !fieldValueStr.startsWith(filterValueLower);
      case "doesnotendwith":
        return !fieldValueStr.endsWith(filterValueLower);
      case "doesnotcontain":
        return !fieldValueStr.includes(filterValueLower);
      case "notequal":
        return fieldValueStr !== filterValueLower;
      case "notnull":
      case "notempty":
        return fieldValueStr !== "";
      default:
        return false;
    }
  };

  const matchNumberFilter = (fieldValue: unknown, filterOption: QueryOperator, filterValue: unknown): boolean => {
    const n = Number(fieldValue);
    const f = Number(filterValue);
    switch (filterOption) {
      case "equal":
        return n === f;
      case "greaterthan":
        return n > f;
      case "greaterthanorequal":
        return n >= f;
      case "lessthan":
        return n < f;
      case "lessthanorequal":
        return n <= f;
      case "notequal":
        return n !== f;
      case "null":
        return Number.isNaN(n);
      case "notnull":
        return !Number.isNaN(n);
      default:
        return false;
    }
  };

  /** Multi-select option item with optional name property */
  type MultiSelectItem = { name?: string } | string | number;

  const matchMultiSelectFilter = (fieldValue: unknown, filterValue: unknown): boolean => {
    const selectedValues: unknown[] = Array.isArray(filterValue) ? filterValue : [filterValue];
    if (Array.isArray(fieldValue)) {
      const names = (fieldValue as MultiSelectItem[]).map((it) =>
        typeof it === "object" && it !== null && "name" in it ? it.name : String(it),
      );
      return selectedValues.some((sv) => names.some((fv) => toLowerString(fv) === toLowerString(sv)));
    }
    return selectedValues.some((sv) => toLowerString(fieldValue) === toLowerString(sv));
  };

  function applyFilterLocal(
    dataArr: T[],
    columnId: string,
    filterOption: QueryOperator,
    filterValue: unknown,
    fType: FilterType,
  ): T[] {
    return dataArr.filter((item) => {
      const fieldValue = get(item, columnId) ?? "";

      switch (fType) {
        case "Date":
          return matchDateFilter(fieldValue, filterOption, filterValue as DateFilterValue);
        case "Text":
          return matchTextFilter(fieldValue, filterOption, filterValue);
        case "Number":
          return matchNumberFilter(fieldValue, filterOption, filterValue);
        case "MultiSelect":
          return matchMultiSelectFilter(fieldValue, filterValue);
        default:
          return false;
      }
    });
  }

  /** Date range filter text value */
  interface DateRangeText {
    startDate?: Dayjs;
    endDate?: Dayjs;
    date?: Dayjs;
  }

  const getUpdatedFilterConditions = (conds: Record<string, FilterCondition>): Record<string, FilterCondition> => {
    const updated = Object.keys(conds).reduce<Record<string, FilterCondition>>((result, filterKey) => {
      const filterValue = conds[filterKey]!;
      if (filterValue.option === "between" && filterValue.type === "Date") {
        const { text, ...otherFields } = filterValue;
        const dateText = text as DateRangeText | undefined;
        const { startDate, endDate } = dateText || {};
        if (startDate) {
          result[`${filterKey}_start`] = {
            ...otherFields,
            option: "greaterthanorequal",
            text: startDate.format(),
          };
        }
        if (endDate) {
          result[`${filterKey}_end`] = {
            ...otherFields,
            option: "lessthanorequal",
            text: endDate.format(),
          };
        }
      } else if (filterValue.type === "Date" && (filterValue.option === "equal" || !filterValue.option)) {
        const { text, ...otherFields } = filterValue;
        const dateText = text as DateRangeText | undefined;
        const dayjsObject = dayjs();
        const startDate = (dateText?.date ?? dayjsObject).startOf("day").format();
        const endDate = (dateText?.date ?? dayjsObject).endOf("day").format();
        result[`${filterKey}_start`] = { ...otherFields, option: "greaterthanorequal", text: startDate };
        result[`${filterKey}_end`] = { ...otherFields, option: "lessthanorequal", text: endDate };
      } else {
        result[filterKey] = filterValue;
      }
      return result;
    }, {});
    return updated;
  };

  useEffect(() => {
    const grid = gridRef.current?.slickGrid;
    if (!grid) return;

    const renderHeaderTemplate = (_e: unknown, args: { column: Column<T>; node: HTMLElement }) => {
      const col = args.column as ColumnDefinition<T>;
      if (!col?.headerTemplate) return;

      args.node.innerHTML = "";
      const mount = document.createElement("div");
      args.node.appendChild(mount);
      const root = createRoot(mount);
      root.render(
        col.headerTemplate({
          column: col,
          onClick: (_ev: unknown) => {},
        }),
      );
    };

    grid.onHeaderCellRendered.subscribe(renderHeaderTemplate);
    grid.setColumns(grid.getColumns());
    grid.invalidate();
    grid.render();
    return () => {
      grid.onHeaderCellRendered.unsubscribe(renderHeaderTemplate);
    };
  }, [props.columnDefinitions, gridRef]);

  const ensureIds = React.useCallback(
    (arr: T[]): T[] =>
      arr.map((data) => {
        const out: T = { ...data };

        const isIdMissing = out.id === undefined || out.id === null;

        if (isIdMissing) {
          const mappedId = props.idMapping
            ? ((out as Record<string, unknown>)[props.idMapping] as RowId | undefined)
            : undefined;
          const altId = out._id;

          if (mappedId) {
            out.id = mappedId;
          } else if (altId == null) {
            out.id = `row_${crypto.randomUUID()}`;
          } else {
            out.id = altId;
          }
        }
        if (props.idMapping && (out as Record<string, unknown>)[props.idMapping]) {
          out.id = (out as Record<string, unknown>)[props.idMapping] as RowId;
        }
        return out;
      }),
    [props.idMapping],
  );

  const mapTreeGridFlags = React.useCallback(
    (arr: T[]): T[] =>
      props.treeGrid
        ? arr.map((d) => {
            const hasChild = props.hasChildMapping
              ? Boolean((d as Record<string, unknown>)[props.hasChildMapping])
              : false;
            return {
              ...d,
              __hasChildren: hasChild,
              __collapsed: hasChild ? true : undefined,
            } as T;
          })
        : arr,
    [props.treeGrid, props.hasChildMapping],
  );

  const buildModifiedQuery = (skip: number, take: number): QueryShape => {
    const modifiedQuery: QueryShape = { ...props.query, skip, take, requiresCounts: true };

    const hasPredicates =
      filterPredicates && typeof filterPredicates === "object" && Object.keys(filterPredicates).length > 0;

    if (hasPredicates) {
      if (props.query?.where?.length) {
        modifiedQuery.where = [...(props.query.where || []), filterPredicates];
      } else {
        modifiedQuery.where = [filterPredicates];
      }
    }
    if (sortingDetails) {
      modifiedQuery.sorted = [
        { name: sortingDetails.columnId, direction: sortingDetails.sortAsc ? "ascending" : "descending" },
      ];
    }
    if (nextPageTokenRef.current) {
      modifiedQuery.nextPageToken = nextPageTokenRef.current;
    }
    return modifiedQuery;
  };

  interface RemoteDataResponse {
    data?: {
      result?: T[];
      nextPageToken?: string;
      isLast?: boolean;
      count?: number;
    };
    result?: T[];
    nextPageToken?: string;
    isLast?: boolean;
    count?: number;
  }

  const processRemoteResponse = (response: { data?: RemoteDataResponse }): T[] => {
    const current = gridRef.current;
    if (current) current.actual = response;

    let modifiedResponse: T[];
    if (props.beforeDataBound) {
      modifiedResponse = props.beforeDataBound(response);
    } else {
      modifiedResponse = response?.data?.result ?? [];
    }
    if (response?.data?.nextPageToken) nextPageTokenRef.current = response.data.nextPageToken;
    if (!isNil(response?.data?.isLast)) isLastRef.current = Boolean(response.data.isLast);
    if (!isNil(response?.data?.count)) setTotalItemCount(Number(response.data.count));
    return modifiedResponse;
  };

  const processLocalDataset = (skip: number, take: number): T[] => {
    const updatedFilterConds = getUpdatedFilterConditions(filterConditions);

    const applyFiltersToData = (arr: T[]): T[] => {
      if (!appliedFilters.length) return arr;
      return Object.keys(updatedFilterConds).reduce((currentData: T[], filter: string) => {
        const cond = updatedFilterConds[filter] as FilterCondition;
        return applyFilterLocal(
          currentData,
          cond.id,
          cond.option || DEFAULT_FILTER_OPTIONS[cond.type as keyof typeof DEFAULT_FILTER_OPTIONS].option,
          cond.text,
          cond.type,
        );
      }, arr);
    };

    const includeMissingParentsForTree = (filteredData: T[], items: T[]): T[] => {
      if (!props.treeGrid) return filteredData;
      const parentKey = props.parentIdMapping as keyof T;
      const idMap = new Map<RowId, T>(items.map((it) => [it.id!, it]));
      const neededIds = new Set<RowId>(filteredData.map((d) => d.id!));

      const buildParentChain = (child: T): T[] => {
        const chain: T[] = [];
        let current: T | undefined = child;
        while (current?.[parentKey]) {
          const parentId = current[parentKey] as unknown as RowId;
          if (neededIds.has(parentId)) break;
          const parent = idMap.get(parentId);
          if (!parent) break;
          neededIds.add(parentId);
          chain.push(parent);
          current = parent;
        }
        return chain;
      };

      const appendUnique = (target: T[], seenSet: Set<RowId>, toAppend: T[]) => {
        for (let i = toAppend.length - 1; i >= 0; i--) {
          const p = toAppend[i]!;
          if (!seenSet.has(p.id!)) {
            target.push(p);
            seenSet.add(p.id!);
          }
        }
      };

      const missingParentData: T[] = [];
      const addedParentIds = new Set<RowId>();

      for (const child of filteredData) {
        const chain = buildParentChain(child);
        appendUnique(missingParentData, addedParentIds, chain);
      }

      const out: T[] = [];
      const seen = new Set<RowId>();
      for (const item of [...missingParentData, ...filteredData]) {
        if (!seen.has(item.id!)) {
          seen.add(item.id!);
          out.push(item);
        }
      }
      return out;
    };

    let modifiedResponse: T[];
    const filtered = applyFiltersToData(props.dataset || []);

    if (appliedFilters.length) {
      let filteredData = filtered;
      if (props.treeGrid) {
        filteredData = includeMissingParentsForTree(filteredData, dataset);
      }
      modifiedResponse = filteredData;
      setTotalItemCount(filteredData.length);
    } else {
      modifiedResponse = props.dataset || [];
      setTotalItemCount(modifiedResponse.length);
    }

    if (sortingDetails) {
      const col = props.columnDefinitions.find((c) => c.id === sortingDetails.columnId);
      const getter = col?._valueGetter;
      modifiedResponse = [...modifiedResponse].sort((a, b) => {
        const va = getter ? getter(a) : get(a, sortingDetails.columnId);
        const vb = getter ? getter(b) : get(b, sortingDetails.columnId);
        if (va > vb) return sortingDetails.sortAsc ? 1 : -1;
        if (va < vb) return sortingDetails.sortAsc ? -1 : 1;
        return 0;
      });
    }

    return modifiedResponse.slice(skip, take + skip);
  };

  const loadData = async (skip: number, take: number, appendNext = false) => {
    let modifiedResponse: T[] = [];

    if (props.query && props.remoteDataSource) {
      const modifiedQuery = buildModifiedQuery(skip, take);
      // Dynamic remoteDataSource URL - endpoint not known at compile time
      const response = await axiosWrapper.post<RemoteDataResponse>(props.remoteDataSource, { value: modifiedQuery });
      modifiedResponse = processRemoteResponse(response);
    } else if (props.dataset) {
      modifiedResponse = processLocalDataset(skip, take);
    }

    if (props.treeGrid && modifiedResponse?.length) {
      modifiedResponse = mapTreeGridFlags(modifiedResponse);
    }

    modifiedResponse = ensureIds(modifiedResponse);

    setLoaderOnPageChange(false);

    if (appendNext) {
      setDataset((prev) => [...(prev || []), ...modifiedResponse]);
      setLoaderForInfiniteScroll(false);
    } else {
      setDataset(modifiedResponse);
    }

    // Best-effort housekeeping, deliberately non-fatal. The previous `if (…?.setSelectedRows)` test
    // only proved the METHOD exists — it always does, and it throws from inside when the grid has no
    // selection model registered (see ../gridSelection). That throw escaped `loadData` two lines
    // above `setGridReady`, so the grid stayed mounted but never announced itself and rendered as
    // blank rows — the reported TreeView / TreeDropDown / template-picker symptom, captured in
    // Sentry as CUBE-FRONTEND-E3E and CUBE-FRONTEND-ECB.
    setSelectedRowsSafe(gridRef.current?.slickGrid, []);

    props.setGridReady?.(true);
  };

  const handleDataLoad = () => {
    if (props.allowPagination) {
      loadData((currentPage - 1) * pageSize, pageSize);
    } else if (props.infiniteScroll) {
      loadData(0, FETCH_SIZE);
    } else {
      loadData(0, NO_OF_FETCHED_ROWS);
    }
  };

  useEffect(() => {
    setLoaderOnPageChange(true);
    const timeout = setTimeout(() => {
      handleDataLoad();
    }, 0);
    return () => clearTimeout(timeout);
  }, [pageSize, currentPage, props.query, props.dataset, sortingDetails, appliedFilters]);

  useEffect(() => {
    if (props.treeGrid && !props.disableTreeCollapseOnDataChange) {
      gridRef.current?.treeDataService?.toggleTreeDataCollapse?.(false);
    }
  }, [dataset, props.treeGrid, props.disableTreeCollapseOnDataChange, gridRef]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    const keysToStop = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "v", "c", "x"];
    if (keysToStop.includes(e.code)) {
      e.stopPropagation();
    }
  };

  interface ScrollEventDetail {
    detail: {
      args: OnScrollEventArgs;
    };
  }

  const handleOnScroll = debounce(($event: ScrollEventDetail) => {
    const args = $event.detail.args;
    const viewportElm = args.grid.getViewportNode() as HTMLElement;

    if (
      ["mousewheel", "scroll"].includes(args.triggeredBy || "") &&
      viewportElm.scrollTop > 0 &&
      Math.ceil(viewportElm.offsetHeight + args.scrollTop) >= args.scrollHeight
    ) {
      const startIdx = gridRef.current?.dataView?.getItemCount() || 0;
      const st = stateRef.current;
      if (st && st.totalItemCount >= st.dataset.length) {
        if (!isLastRef.current) {
          setLoaderForInfiniteScroll(true);
          loadData(startIdx, FETCH_SIZE, true);
        }
      }
    }
  }, 200);

  useEffect(() => {
    const renderEditorTemplate = (updatedColumn: ColumnDefinition<T>, args: T) => {
      const value = updatedColumn._valueGetter
        ? updatedColumn._valueGetter(args)
        : get(args, updatedColumn.field as string);
      const container = document.createElement("div");
      container.classList.add("w-full");
      const root = createRoot(container);
      root.render(
        <Box className="w-full h-full flex" onKeyDown={handleKeyPress}>
          {updatedColumn.editorTemplate?.({ value, updatedColumn, args })}
        </Box>,
      );
      return container;
    };

    const columnDefinitions = props.columnDefinitions.map((column) => {
      let updated: ColumnDefinition<T> = {
        sortable: !props.isEditMode,
        excelExportOptions: {
          valueParserCallback: (_: unknown, { dataContext }: { dataContext: T }) =>
            column._valueGetter ? column._valueGetter(dataContext) : get(dataContext, column.field as string, ""),
        },
        ...column,
      };

      updated.formatter = buildBaseFormatter(column as ColumnDefinition<T>, !!props.isEditMode);

      if (props.isEditMode && updated.editorTemplate) {
        updated.formatter = (
          row: number,
          _col: number,
          _value: unknown,
          _columnDef: unknown,
          args: T,
          grid?: unknown,
        ) => {
          const selectedRow = (gridRef.current?.slickGrid as SlickGridType | undefined)?.getSelectedRows?.()?.[0];
          if (selectedRow === row) {
            return renderEditorTemplate(updated, args);
          }
          if (column.formatter) {
            return column.formatter(row, _col, _value as T[keyof T], _columnDef, args, grid);
          }
          if (column._valueGetter) return column._valueGetter(args);
          return get(args, column.field as string);
        };
      }

      if (column.filterType && !props.isEditMode) {
        const buttoncssClass = appliedFilters.includes(String(column.id))
          ? "mdi mdi-filter-variant text-[var(--primary-gradient-color1)]"
          : "mdi mdi-filter-variant text-slate-900";

        updated = {
          filterable: true,
          sortable: true,
          groupable: true,
          type: "string",
          header: {
            ...column.header,
            buttons: [
              {
                cssClass: buttoncssClass,
                command: "toggle-highlight",
                tooltip: String(column.id),
              },
            ],
          },
          ...updated,
        };
      }

      return updated;
    });

    if (props.isEditMode) {
      setUpdatedColumnDefinitions(columnDefinitions);
    } else {
      setUpdatedColumnDefinitions([...specialColumnRef.current, ...columnDefinitions]);
    }
  }, [props.columnDefinitions, props.isEditMode, appliedFilters, props.preAppliedFilters]);

  useEffect(() => {
    if (!props.dragAndDrop) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let lastTop: number | null = null;

    const checkPosition = () => {
      const el = document.querySelector(".slick-reorder-proxy");
      if (el) {
        const { top } = (el as HTMLElement).getBoundingClientRect();
        if (top !== lastTop) {
          topOfDragRowRef.current = top;
          setTop(top);
          lastTop = top;
        }
      }
    };

    const observer = new MutationObserver(() => {
      const element = document.querySelector(".slick-reorder-proxy");
      if (element && !intervalId) {
        intervalId = setInterval(checkPosition, 100);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (intervalId) clearInterval(intervalId);
    };
  }, [props.dragAndDrop]);

  useEffect(() => {
    if (props.enableWrapText && gridRef.current) {
      const sg = gridRef.current.slickGrid;
      if (sg) {
        sg.onColumnsResized.notify({ triggeredByColumn: "", grid: sg });
      }
    }
  }, [dataset, props.isEditMode, props.enableWrapText, gridRef]);

  const handleFilterIconClick = (
    event: React.MouseEvent<HTMLElement>,
    columnIdArg: string,
    filterTypeArg: FilterType,
  ) => {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    setFilterOpened(true);
    setAnchorEl({ top: rect.top, left: rect.left });
    setCurrentColumnId(columnIdArg);
    setFilterType(filterTypeArg);
  };

  const onBeforeMoveRows = (_e: Event, data: RowMoveData): boolean => {
    setInsertBefore(data.insertBefore);
    setDataRow(data.rows[0] ?? null);

    const firstRow = data.rows[0];
    if (firstRow !== undefined && firstRow < data.insertBefore) return false;
    for (const r of data.rows) {
      if (r === data.insertBefore - 1) {
        _e.stopPropagation();
        return false;
      }
    }
    return true;
  };

  interface DropTargetOptions {
    currentTop: number | undefined;
    insertBeforeIdx: number;
    movedRowElement: Element | null;
    droppedRowElement: Element | null;
    droppedRowData: T | null;
  }

  const determineDropTarget = (opts: DropTargetOptions): { newParentId: ParentId; newInsertPosition: number } => {
    const { currentTop, insertBeforeIdx, movedRowElement, droppedRowElement, droppedRowData } = opts;
    let newParentId: ParentId = null;
    let newInsertPosition = insertBeforeIdx;

    if (!droppedRowElement) {
      return { newParentId, newInsertPosition };
    }

    const movedRectTop = (movedRowElement as HTMLElement | null)?.getBoundingClientRect().top ?? -Infinity;
    const rowRect = (droppedRowElement as HTMLElement).getBoundingClientRect();
    const midpointY = rowRect.top + rowRect.height / 2;

    if (movedRectTop < rowRect.top) {
      return { newParentId, newInsertPosition };
    }

    if (isMidpointHit(currentTop ?? -Infinity, droppedRowElement)) {
      const dropId = droppedRowData?._id;
      newParentId = dropId != null ? (typeof dropId === "object" ? dropId.toString() : dropId) : null;
      newInsertPosition = insertBeforeIdx;
    } else if (
      currentTop !== undefined &&
      currentTop < rowRect.top + rowRect.height &&
      currentTop >= midpointY + DRAG_TOLERANCE_PX
    ) {
      newInsertPosition = insertBeforeIdx;
    }

    return { newParentId, newInsertPosition };
  };

  const reconcileOldParentChildren = (previousParentId: RowId | undefined, treeData: T[]): void => {
    if (!previousParentId) return;
    const oldParent = treeData.find((it) => it._id === previousParentId);
    if (!oldParent) return;
    const hasChildren = treeData.some((it) => it.parentId === oldParent._id);
    if (!hasChildren) {
      oldParent.__hasChildren = false;
      oldParent.hasChild = false;
    }
  };

  const tryApplyRowDrop = async (
    newParentId: ParentId,
    movedRowData: T,
    droppedRowData: T | null,
    previousParentId: RowId | undefined,
    treeData: T[],
  ): Promise<boolean> => {
    setLoaderOnPageChange(true);
    const response = await props.rowDrop?.({ parentId: newParentId ?? null, movedRowData });
    setLoaderOnPageChange(false);
    if (!response) return false;

    movedRowData.parentId = newParentId;
    movedRowData.treeLevel = newParentId ? (droppedRowData?.treeLevel || 0) + 1 : 0;

    if (newParentId && droppedRowData) {
      droppedRowData.__hasChildren = true;
      droppedRowData.hasChild = true;
    }

    reconcileOldParentChildren(previousParentId, treeData);
    return true;
  };

  const onMoveRows = async (_e: Event, args: RowMoveData): Promise<void> => {
    const { rows, insertBefore: insertBeforeIdx } = args;

    if (gridRef.current) {
      gridRef.current.dataView.sort(() => 0, true);
    }

    const currentTop = topOfDragRowRef.current!;
    const row = rows[0];
    if (row == null) return;

    const dv = gridRef.current?.dataView;
    const itemsSource = getItemsSourceSafe(dv);
    const data = [...itemsSource];
    const movedRowData = data[row];
    if (!movedRowData) return;
    const previousParentId = movedRowData.parentId;

    const insertIndex = typeof insertBeforeIdx === "number" ? insertBeforeIdx : null;
    const droppedRowIndex = insertIndex == null || insertIndex === 0 ? null : insertIndex - 1;
    const droppedRowData = droppedRowIndex == null ? null : (data[droppedRowIndex] ?? null);

    const movedRowElement = getRowElementByIndex(row);
    const droppedRowElement = droppedRowIndex == null ? null : getRowElementByIndex(droppedRowIndex);

    const { newParentId, newInsertPosition } = determineDropTarget({
      currentTop,
      insertBeforeIdx,
      movedRowElement,
      droppedRowElement,
      droppedRowData,
    });

    let treeData = gridRef.current?.dataView?.getItems?.() ?? [];

    if (newParentId || insertBeforeIdx === 0) {
      await tryApplyRowDrop(newParentId ?? null, movedRowData, droppedRowData, previousParentId, treeData);
    }

    treeData = treeData.filter((it) => it._id !== movedRowData._id);
    treeData.splice(newInsertPosition, 0, movedRowData);

    setDataset(treeData as T[]);
    gridRef.current?.slickGrid.invalidate();
  };

  const handleDrag = (): void => {
    if (!gridRef.current) return;

    const reorderGuide = document.querySelector<HTMLElement>(".slick-reorder-guide");
    if (!reorderGuide) return;

    const dv = gridRef.current.dataView;
    const itemsSource = getItemsSourceSafe(dv);
    const dataRows = [...itemsSource];

    const currentTop = topOfDragRowRef.current!;
    const iBefore = insertBefore as number;

    if (dataRow == null || dataRow === iBefore - 1) return;

    const droppedRowIndex = getDroppedRowIndex(iBefore);
    if (droppedRowIndex == null) return;

    const rowEls = getRowElementsSafe(dataRow, droppedRowIndex);
    if (!rowEls) return;

    const { movedEl, droppedEl } = rowEls;
    const movedElTop = (movedEl as HTMLElement).getBoundingClientRect().top;

    const rowRect = (droppedEl as HTMLElement).getBoundingClientRect();
    const rowRectTop = rowRect.top;

    const droppedRowData = dataRows[droppedRowIndex] ?? null;
    const movedRowData = dataRows[dataRow];

    const hide = shouldHideGuide({
      movedElTop,
      rowRectTop,
      droppedRowData,
      movedRowData,
      currentTop,
      droppedEl,
      insertBefore: iBefore,
    });

    if (hide) {
      setGuide(reorderGuide, false, false);
      return;
    }

    const midpoint = isMidpointHit(currentTop, droppedEl);
    setGuide(reorderGuide, true, midpoint);
  };

  useEffect(() => {
    handleDrag();
  }, [top]);

  useEffect(() => {
    const grid = gridRef.current?.slickGrid;
    const dv = gridRef.current?.dataView;
    if (!grid || !dv) return;

    const KEY = "disabled-rows";

    const applyDisabledStyles = () => {
      grid.removeCellCssStyles(KEY);

      if (!Array.isArray(props.disabledIds) || !props.disabledIds.length) return;

      const cols = grid.getColumns();
      const styles: Record<number, Record<string, string>> = {};

      for (const id of props.disabledIds) {
        const rowIdx = dv.getRowById(String(id));
        if (rowIdx == null || rowIdx < 0) continue;

        styles[rowIdx] = {};
        for (const col of cols) {
          styles[rowIdx][col.id] = "slick-disabled-row";
        }
      }

      grid.setCellCssStyles(KEY, styles);
    };

    applyDisabledStyles();

    const rerun = () => applyDisabledStyles();
    dv.onRowsChanged.subscribe(rerun);
    dv.onRowCountChanged.subscribe(rerun);

    return () => {
      grid.removeCellCssStyles(KEY);
      dv.onRowsChanged.unsubscribe(rerun);
      dv.onRowCountChanged.unsubscribe(rerun);
    };
  }, [props.disabledIds, gridRef]);

  const onSortCallback = useCallback(
    (_e: unknown, args: unknown) => {
      if (!props.disableSorting && args && typeof args === "object") {
        const sortArgs = args as { columnId?: string | number; sortAsc?: boolean };
        const columnId = String(sortArgs.columnId ?? "");
        const sortAsc = Boolean(sortArgs.sortAsc);
        setSortingDetails({ columnId, sortAsc });
      }
    },
    [props.disableSorting],
  );

  interface ExternalResource {
    className?: string;
  }

  interface ExtensionServiceWithShared {
    sharedService?: {
      externalRegisteredResources?: ExternalResource[];
    };
  }

  const reactGridReady = (reactGrid: SlickgridReactInstance): void => {
    gridRef.current = reactGrid as SlickgridReactInstanceX<T>;

    const extensionService = reactGrid.extensionService as unknown as ExtensionServiceWithShared;
    const exportService = extensionService.sharedService?.externalRegisteredResources?.find(
      (service) => service.className === "ExcelExportService",
    );
    if (exportService) setExcelExportExtension(exportService as unknown as ExcelExportService);

    setGridMounted(true);
    reactGrid.slickGrid?.invalidate();

    if (!props.disableCustomSorting) {
      reactGrid.slickGrid.onSort.subscribe(onSortCallback);
    }

    specialColumnRef.current = reactGrid.slickGrid
      .getColumns()
      .filter((c) => ["_checkbox_selector", "_move"].includes(c.id as string))
      .map(mapSpecialColumn) as unknown as ColumnDefinition<T>[];

    if (props.enableWrapText) {
      setTimeout(() => {
        handleWrapUpdate();
      }, INITIAL_WRAP_RUN_DELAY_MS);
    }

    if (props.treeGrid) {
      type FilterServiceWithDisable = { disableFilterFunctionality?: () => void };
      (
        reactGrid as unknown as { filterService?: FilterServiceWithDisable }
      )?.filterService?.disableFilterFunctionality?.();
    }
    reactGrid.slickGrid.onColumnsReordered.subscribe((e, args) => {
      const cols = [...(gridRef.current?.slickGrid.getColumns() ?? [])];
      const corrected = [
        cols.find((c) => c.id === "_checkbox_selector"),
        cols.find((c) => c.id === "_move"),
        ...cols.filter((c) => !["_checkbox_selector", "_move"].includes(c.id as string)),
      ].filter(Boolean);

      gridRef.current?.slickGrid.setColumns(corrected as Column<T>[]);

      if (props.onColumnsReordered) {
        props.onColumnsReordered(e as unknown as Event, args as ColumnsReorderedArgs);
      }
    });

    props.onReactGridCreated?.(reactGrid);
  };

  interface SelectedRowIdsChangedEvent {
    detail: {
      args: {
        selectedRowIds: RowId[];
      };
    };
  }

  const onSelectedRowIdsChanged = (args: SelectedRowIdsChangedEvent): void => {
    setSelectedItems(args.detail.args.selectedRowIds.length);
    props.onSelectedRowIdsChanged?.(args as unknown as SlickEventArgs);
  };

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.appliedFilters = appliedFilters;
      if (props.onFilterChange && appliedFilters.length > 0) {
        props.onFilterChange(appliedFilters, filterConditions);
      }
    }
  }, [appliedFilters, filterConditions, props, gridRef]);

  useEffect(() => {
    if (props.preAppliedFilters && Object.keys(props.preAppliedFilters).length > 0) {
      setAppliedFilters(Object.keys(props.preAppliedFilters));
      setFilterConditions(props.preAppliedFilters);
    }
  }, [props.preAppliedFilters]);

  useEffect(() => {
    const current = gridRef.current;
    if (current?.deleteRow) {
      current.deleteRow = false;
      current.slickGrid?.setSelectedRows([]);
    }
  }, [gridRef]);

  const getChildData = async (idParam: string | number): Promise<T[]> => {
    const modifiedQuery: QueryShape = { ...props.query };
    const predicate: QueryPredicate = {
      field: String(props.parentIdMapping),
      operator: "equal",
      value: idParam,
      isComplex: false,
      ignoreCase: true,
      ignoreAccent: false,
    };
    modifiedQuery.where = [predicate];
    // Dynamic remoteDataSource URL - endpoint not known at compile time
    const response = await axiosWrapper.post<RemoteDataResponse>(props.remoteDataSource!, {
      value: modifiedQuery,
    });
    const arr: T[] = props.beforeDataBound ? props.beforeDataBound(response) : (response?.data?.result ?? []);
    return arr.map((d) => {
      if (!d.id) d.id = d._id;
      return d;
    });
  };

  useEffect(() => {
    if (gridRef.current && gridMounted && props.surpassedDataset?.length) {
      gridRef.current.dataView.setItems(props.surpassedDataset);
      gridRef.current?.slickGrid?.invalidate();
    }
  }, [props.surpassedDataset, gridMounted, gridRef]);

  const handleRowClickSelection = (rowIndex: number): void => {
    const slickGrid = gridRef.current?.slickGrid;
    const selRows: number[] = slickGrid?.getSelectedRows() ?? [];
    if (selRows.includes(rowIndex)) {
      slickGrid?.setSelectedRows(selRows.filter((row) => row !== rowIndex));
      return;
    }
    const isMulti = Boolean(gridOptions.multiSelect ?? true);
    const next = isMulti ? [...selRows, rowIndex] : [rowIndex];
    slickGrid?.setSelectedRows(next);
  };

  const handleTreeGridClick = async (rowIndex: number, cellIndex: number): Promise<void> => {
    if (!props.treeGrid || props.disableChildDataFetch) return;
    if (cellIndex !== props.dropdownColumn) return;

    const hasChildMapping = props.hasChildMapping!;
    const items = gridRef.current?.dataView?.getItems() ?? [];
    const row = items[rowIndex];
    if (!row || !(row as Record<string, unknown>)[hasChildMapping]) return;

    if (!row.isExpanded) {
      setLoaderOnPageChange(true);
      const indentLevel = row.indentLevel ? row.indentLevel + 1 : 1;
      const dv = gridRef.current!.dataView;
      const data = getItemsSourceSafe(dv);

      let childData = await getChildData(row.id!);
      childData = childData.filter((d) => d._id !== row._id);

      const parentIndex = data.findIndex((r) => r.id === row.id);

      const childRows = childData.map((child) => ({
        ...child,
        indentLevel,
        parentId: row.id,
        [props.hasChildMapping as string]: (child as Record<string, unknown>)[hasChildMapping] || false,
        __hasChildren: (child as Record<string, unknown>)[hasChildMapping] as boolean,
        treeLevel: indentLevel,
        __collapsed: true,
        isExpanded: false,
      })) as T[];

      const updatedData = [...data.slice(0, parentIndex + 1), ...childRows, ...data.slice(parentIndex + 1)];
      const parentItem = updatedData[parentIndex];
      if (parentItem) {
        updatedData[parentIndex] = { ...parentItem, isExpanded: true };
      }

      setDataset([...updatedData]);
      setLoaderOnPageChange(false);
    }
  };

  type ClickEventType = CustomEvent<{ eventData: unknown; args: OnClickEventArgs }>;

  const onClick = (e: ClickEventType): void => {
    if (props.selectCheckBoxOnRowClick && !isEditModeRef.current) {
      handleRowClickSelection(e.detail.args.row);
    }
    props.onClick?.(e as unknown as SlickEventArgs, {} as SlickEventArgs);
    void handleTreeGridClick(e.detail.args.row, e.detail.args.cell);
  };

  const onDblClick = (e: ClickEventType): void => {
    props.onDblClick?.(e);
  };

  interface HeaderButtonCommandArgs {
    column: {
      field: string;
      filterType: FilterType;
    };
  }

  interface PubSubServiceType {
    publish: (event: string, data?: unknown) => void;
    subscribe: (event: string, callback: (data: unknown) => void) => void;
  }

  const gridOptions: GridOption = React.useMemo(() => {
    const base: GridOption = {
      sanitizer: (dirtyHtml: string) => DOMPurify.sanitize(dirtyHtml),
      enableContextMenu: false,
      enableHeaderButton: true,
      enableGridMenu: props.enableGridMenu ?? false,
      enableColumnPicker: props.enableColumnPicker ?? true,
      showCustomFooter: false,
      enableHeaderMenu: false,
      enableSorting: false,
      enableCellRowSpan: true,
      rowTopOffsetRenderType: "top",
      enableColumnReorder: true,
      enableCellNavigation: !props.isEditMode,
      headerButton: {
        onCommand: ((_e: unknown, args: unknown) => {
          const typedArgs = args as HeaderButtonCommandArgs;
          handleFilterIconClick(
            _e as React.MouseEvent<HTMLElement>,
            typedArgs.column.field,
            typedArgs.column.filterType,
          );
        }) as NonNullable<GridOption["headerButton"]>["onCommand"],
      },
      gridMenu: {
        onCommand: (_e: unknown) => {},
      },
      ...(props.hasChild && {
        preRegisterExternalExtensions: (pubSubService: PubSubServiceType) => {
          const rowDetail = new SlickRowDetailView(
            pubSubService as typeof pubSubService & ConstructorParameters<typeof SlickRowDetailView>[0],
          );
          return [{ name: ExtensionName.rowDetailView, instance: rowDetail }];
        },
      }),
      externalResources: [excelExportService],
      enableExcelExport: true,
      enableExcelCopyBuffer: true,
      createPreHeaderPanel: true,
      preHeaderPanelHeight: 28,
      rowHeight: 48,
      multiColumnSort: false,
      multiSelect: props.enableMultiSelect ?? true,
      enableAutoSizeColumns: true,
      enableAutoResize: true,
      enableFiltering: Object.hasOwn(props, "enableFiltering") ? !!props.enableFiltering : false,
      enableDraggableGrouping: props.allowGrouping ?? false,
      enableRowSelection: !props.isEditMode,
      createTopHeaderPanel: true,
      topHeaderPanelHeight: 35,
      enableCheckboxSelector: !props.isEditMode,
      checkboxSelector: { toolTip: "" },
      rowSelectionOptions: { selectActiveRow: false },
      ...(props.treeGrid && {
        enableTreeData: true,
        treeDataOptions: {
          columnId: String(props.initialSortColumnId ?? ""),
          parentPropName: String(props.parentIdMapping ?? ""),
          levelPropName: "treeLevel",
          indentMarginLeft: 25,
          initiallyCollapsed: true,
          initialSort: {
            columnId: String(props.initialSortColumnId ?? ""),
            direction: (props.initialSortDirection as "ASC" | "DESC") || "DESC",
          },
          titleFormatter: props.titleFormatter,
        },
      }),
      ...(props.dragAndDrop && {
        enableRowMoveManager: true,
        rowMoveManager: {
          columnIndexPosition: 0,
          cancelEditOnDrag: true,
          disableRowSelection: true,
          hideRowMoveShadow: false,
          onBeforeMoveRows,
          onMoveRows: (e: Event, args: RowMoveData) => {
            void onMoveRows(e, args);
          },
        },
      }),
      autoResize: {
        container: `#${id.replaceAll(":", "_")}`,
        resizeDetection: "container",
        calculateAvailableSizeBy: "container",
      },
      autoHeight: !!props.hasChild,
      ...props.gridOptions,
    };

    if (props.allowGrouping) {
      type GridOptionWithDraggable = GridOption & {
        draggableGrouping?: {
          dropPlaceHolderText: string;
          deleteIconCssClass: string;
          sortAscIconCssClass: string;
          sortDescIconCssClass: string;
          onGroupChanged?: (args: unknown) => void;
          onExtensionRegistered?: (ext: unknown) => void;
        };
      };
      (base as GridOptionWithDraggable).draggableGrouping = {
        dropPlaceHolderText: "Drop a column header here to group by the column",
        deleteIconCssClass: "mdi mdi-close text-color-danger",
        sortAscIconCssClass: "mdi mdi-arrow-up",
        sortDescIconCssClass: "mdi mdi-arrow-down",
        onGroupChanged: props.onGroupChanged,
        onExtensionRegistered: props.onExtensionRegistered,
      };
    }

    return base;
  }, [excelExportService, id, props]);

  return (
    <div dir="ltr" className="h-full min-w-full cube-slick-grid">
      {!allPrerequisiteLoaded && (
        <div className="w-full pt-4 bg-white">
          <Shimmer />
        </div>
      )}

      {allPrerequisiteLoaded && (
        <div className="flex flex-col h-full min-w-full">
          {props.toolbar && (
            <ToolBar
              toolbar={props.toolbar || []}
              toolbarClickHandler={props.toolbarClickHandler}
              showToolbarBorder={props.showToolbarBorder}
              enableColumnChooser={props.enableColumnChooser}
              // ToolBar declares its own, narrower column/ref shapes; the three assertions below are
              // that single boundary. `ref` in particular is declared `HTMLDivElement` there but is
              // handed the grid instance - see the KNOWN BUG note in ToolBar.tsx.
              columnDefinitions={updatedColumnDefinitions as typeof updatedColumnDefinitions & ToolBarColumn[]}
              callbackForVisibleColumnsUpdate={
                props.callbackForVisibleColumnsUpdate as typeof props.callbackForVisibleColumnsUpdate &
                  ((columns: ToolBarColumn[]) => void)
              }
              ref={gridRef as typeof gridRef & React.Ref<HTMLDivElement>}
              height={
                typeof props.toolbarHeight === "string" ? Number.parseFloat(props.toolbarHeight) : props.toolbarHeight
              }
              propertiesPanel={props.propertiesPanel}
              excelExportExtension={excelExportExtension}
              excelExport={!!props.excelExport}
            />
          )}

          <div
            data-testid="slickGrid"
            id={id.replaceAll(":", "_")}
            className={"relative flex h-full min-w-full overflow-auto" + (cssClasses ? " " + cssClasses : "")}
          >
            {/* SlickgridReact library types differ from our strict props - cast at library boundary */}
            {React.createElement(
              SlickgridReact as typeof SlickgridReact & React.ComponentType<Record<string, unknown>>,
              {
                ...(props as unknown as Record<string, unknown>),
                ...(!props.datasetHierarchical && !props.surpassedDataset ? { dataset } : {}),
                gridId: `${id.replaceAll(":", "_")}_grid18`,
                gridOptions,
                onReactGridCreated: ($event: { detail: SlickgridReactInstance }) => {
                  reactGridReady($event.detail);
                  type SlickGridInternal = SlickGridType & {
                    _paneHeaderL?: HTMLElement;
                    _paneTopL?: HTMLElement;
                  };
                  const slickGrid = $event.detail.slickGrid as SlickGridInternal;
                  slickGrid._paneHeaderL?.setAttribute("data-testid", "SlickGridPaneHeader");
                  slickGrid._paneTopL?.setAttribute("data-testid", "SlickGridPaneContent");
                },
                columnDefinitions: updatedColumnDefinitions as Column<T>[],
                setTotalItemCount,
                onClick,
                onDblClick,
                onSelectedRowIdsChanged,
                ...(props.infiniteScroll ? { onScroll: handleOnScroll } : {}),
                onAfterSetColumns: () => {
                  if (gridRef.current) {
                    if (props.handleUpdateCurrentView) {
                      props.handleUpdateCurrentView({
                        columns: gridRef.current.slickGrid.getColumns().map((c) => c.id as string | number),
                      });
                    }
                    gridRef.current.dataView.refresh();
                  }
                },
              },
            )}
            {(loaderOnPageChange || props.showLoader) && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-[var(--surface-white)] opacity-70 pointer-events-auto">
                <div className="flex items-center justify-center">
                  <CircularProgress />
                </div>
              </div>
            )}
          </div>

          {showPageCount && (
            <div className="!h-[3.125rem] w-full flex items-center justify-between p-3 border-b sticky bottom-0 bg-white shadow-md">
              <PagerComponent
                ref={gridRef as unknown as React.Ref<HTMLDivElement>}
                pageSize={pageSize}
                currentPage={currentPage}
                totalItemCount={totalItemCount}
                setCurrentPage={setCurrentPage}
                setPageSize={setPageSize}
                totalPages={totalPages}
                allowPagination={!!props.allowPagination}
                selectedItems={selectedItems}
                currentLoadedCount={currentLoadedCount}
                infiniteScroll={props.infiniteScroll}
              />
            </div>
          )}

          <FilterComponent
            currentColumnId={currentColumnId}
            setCurrentColumnId={setCurrentColumnId}
            setFilterPredicates={setFilterPredicates}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            filterType={filterType || undefined}
            filterOpened={filterOpened}
            renderOption={props.renderOption}
            setFilterOpened={setFilterOpened}
            datasetForFetchingOptions={
              appliedFilters.length === 0 || (appliedFilters.length === 1 && appliedFilters[0] === currentColumnId)
                ? (props.dataset ?? [])
                : (dataset ?? [])
            }
            setAppliedFilters={setAppliedFilters}
            appliedFilters={appliedFilters}
            // FilterComponent declares its own `{ field, _valueGetter, header }` column shape and a
            // looser applyFilter signature; asserted here at that single boundary.
            columnDefinitions={props.columnDefinitions as typeof props.columnDefinitions & FilterColumn[]}
            beforeDataBound={props.beforeDataBound}
            localData={!props.remoteDataSource}
            ref={gridRef}
            remoteDataSource={props.remoteDataSource}
            query={props.query}
            filterConditions={filterConditions}
            setFilterConditions={setFilterConditions}
            getUpdatedFilterConditions={getUpdatedFilterConditions}
            applyFilter={applyFilterLocal as typeof applyFilterLocal & FilterComponentProps["applyFilter"]}
            preAppliedFilters={props.preAppliedFilters}
            onFilterConditionsChange={props.onFilterChange}
          />
        </div>
      )}

      {loaderForInfiniteScroll && (
        <output
          aria-live="polite"
          className="
            absolute bottom-10 start-1/2 -translate-x-1/2
            inline-flex items-center justify-center gap-2
            rounded-full bg-white/90 px-4 py-2
            text-lg font-medium text-slate-800
            shadow-lg shadow-black/5 ring-1 ring-slate-900/5 backdrop-blur-sm
            dark:bg-slate-800/90 dark:text-slate-200 dark:ring-white/10
          "
        >
          <CircularProgress size={18} thickness={5} sx={{ color: "primary.main" }} />
          <span className="text-xs font-semibold text-slate-800 dark:text-slate-100">
            <Text>{t("Loading more…")}</Text>
          </span>
        </output>
      )}
    </div>
  );
  // `forwardRef` cannot carry the render function's own generic parameter, so the generic is erased
  // through the call and re-stated as an assertion on the result — the same shape the public
  // `SlickGrid` wrapper below asserts, so the two agree and the inner component stays checked.
}) as <T extends GridRowBase>(
  props: SlickGridProps<T> & { ref?: React.Ref<SlickgridReactInstanceX<T>> },
) => React.ReactElement;

/**
 * Type-safe SlickGrid component.
 *
 * IMPORTANT: Use `typedColumns<YourRowType>()` to create column definitions
 * for proper type safety.
 *
 * @example
 * interface MyRow extends GridRowBase {
 *   name: string;
 *   count: number;
 * }
 *
 * const columns = typedColumns<MyRow>([
 *   { id: "name", field: "name", name: "Name" },
 *   { id: "count", field: "count", name: "Count" }
 * ]);
 *
 * <SlickGrid
 *   dataset={myData}
 *   columnDefinitions={columns}
 * />
 */
const SlickGrid = forwardRef(function SlickGrid<T extends GridRowBase>(
  props: Omit<SlickGridProps<T>, "ref">,
  ref: React.Ref<SlickgridReactInstanceX<T>>,
) {
  return <SlickGridInner {...props} ref={ref} />;
}) as <T extends GridRowBase>(
  props: SlickGridProps<T> & { ref?: React.Ref<SlickgridReactInstanceX<T>> },
) => React.ReactElement;

export default SlickGrid;
